/**
 * Buyer Offer Card Component
 * 구매자가 제출한 제안들을 관리하는 카드
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { CheckCircle, Loader2, MessageCircle } from "lucide-react";
import { useState } from "react";
import { DealContactPanel } from "./DealContactPanel";
import { ThreadView } from "./ThreadView";

interface Offer {
  id: number;
  product_id: number;
  buyer_id: string;
  seller_id: string;
  price_cents: number | null;
  counter_price_cents?: number | null;
  pickup_place_custom?: string | null;
  pickup_time_custom?: string | null;
  payment_method?: string | null;
  memo?: string | null;
  status:
    | "pending"
    | "accepted"
    | "countered"
    | "rejected"
    | "confirmed"
    | "completed"
    | "expired";
  confirm_pin?: string;
  dm_thread_id?: string;
  created_at: number;
  updated_at: number;
}

interface BuyerOfferCardProps {
  productId: number;
  buyerId: string;
}

export function BuyerOfferCard({ productId, buyerId }: BuyerOfferCardProps) {
  const { toast } = useToast();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<number | null>(
    null
  );
  const [threadViewOpen, setThreadViewOpen] = useState<number | null>(null);

  // Fetch offers
  const { data: offers = [], isLoading } = useQuery<Offer[]>({
    queryKey: ["/api/market/offers", "buyer", productId, buyerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("role", "buyer");
      if (productId) params.append("product_id", String(productId));

      const res = await fetch(`/api/market/offers?${params.toString()}`);
      if (!res.ok) {
        if (res.status === 404) return [];
        throw new Error("Failed to fetch offers");
      }
      const json = await res.json();
      return Array.isArray(json.items)
        ? json.items
        : Array.isArray(json.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];
    },
    enabled: !!productId && !!buyerId,
  });

  // Confirm offer
  const confirmOfferMutation = useMutation({
    mutationFn: async (offerId: number) => {
      return apiRequest(
        "POST",
        `/api/market/offers/${offerId}?action=confirm`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers", "buyer", productId, buyerId],
      });
      toast({
        title: "거래 확정",
        description: "거래가 확정되었습니다. PIN이 생성되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "확정 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const formatPrice = (cents: number | null) => {
    if (!cents) return "가격 협의";
    return `$${(cents / 100).toFixed(0)} AUD`;
  };

  const getStatusBadge = (status: Offer["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline">대기중</Badge>;
      case "accepted":
        return <Badge className="bg-green-600">수락됨</Badge>;
      case "countered":
        return <Badge className="bg-blue-600">카운터 제안</Badge>;
      case "rejected":
        return <Badge variant="destructive">거절됨</Badge>;
      case "confirmed":
        return <Badge className="bg-purple-600">거래 확정</Badge>;
      case "completed":
        return <Badge className="bg-green-700">거래 완료</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>내 제안</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            로딩 중...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (offers.length === 0) {
    return null; // 제안이 없으면 표시하지 않음
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>내 제안 ({offers.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {offers.map((offer) => (
          <div key={offer.id} className="border rounded-lg p-4 space-y-3">
            {/* Status */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(offer.created_at), {
                  addSuffix: true,
                  locale: ko,
                })}
              </p>
              {getStatusBadge(offer.status)}
            </div>

            <div className="space-y-2 text-sm">
              {offer.price_cents && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">제안 가격:</span>
                  <span className="font-semibold">
                    {formatPrice(offer.price_cents)}
                  </span>
                </div>
              )}
              {offer.counter_price_cents && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">카운터 가격:</span>
                  <span className="font-semibold text-blue-600">
                    {formatPrice(offer.counter_price_cents)}
                  </span>
                </div>
              )}
              {offer.pickup_place_custom && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">픽업 장소:</span>
                  <span>{offer.pickup_place_custom}</span>
                </div>
              )}
              {offer.pickup_time_custom && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">픽업 시간:</span>
                  <span>{offer.pickup_time_custom}</span>
                </div>
              )}
              {offer.payment_method && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">결제 방법:</span>
                  <span>{offer.payment_method}</span>
                </div>
              )}
              {offer.memo && (
                <div>
                  <span className="text-muted-foreground">메모:</span>
                  <p className="mt-1">{offer.memo}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            {offer.status === "accepted" || offer.status === "countered" ? (
              <div className="pt-2">
                <Button
                  size="sm"
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    confirmOfferMutation.mutate(offer.id);
                  }}
                  disabled={confirmOfferMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  거래 확정하기
                </Button>
              </div>
            ) : null}

            {offer.status === "confirmed" && (
              <div className="pt-2 space-y-2">
                <Dialog
                  open={confirmDialogOpen === offer.id}
                  onOpenChange={(open) =>
                    setConfirmDialogOpen(open ? offer.id : null)
                  }
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="w-full">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      거래 정보 보기
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>거래 확정 정보</DialogTitle>
                    </DialogHeader>
                    <DealContactPanel
                      offerId={offer.id}
                      confirmPin={offer.confirm_pin}
                      onComplete={() => {
                        setConfirmDialogOpen(null);
                        queryClient.invalidateQueries({
                          queryKey: [
                            "/api/market/offers",
                            "buyer",
                            productId,
                            buyerId,
                          ],
                        });
                      }}
                    />
                  </DialogContent>
                </Dialog>

                {offer.dm_thread_id && (
                  <Dialog
                    open={threadViewOpen === offer.id}
                    onOpenChange={(open) =>
                      setThreadViewOpen(open ? offer.id : null)
                    }
                  >
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="w-full">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        메시지 보기
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh]">
                      <ThreadView
                        threadId={offer.dm_thread_id}
                        offerId={offer.id}
                        onClose={() => setThreadViewOpen(null)}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}

            {offer.status === "completed" && (
              <div className="pt-2">
                <Badge className="bg-green-600">거래 완료</Badge>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
